/**
 * Mock Teachers, Staff Roles, and Teacher-Role Assignments
 */

import type {
  CpStaffRole,
  CpTeacher,
  CpTeacherRole,
  CpTeacherSubject,
} from '../../../types/curriculum_planning';

const now = Date.now();

export const mockCpStaffRoles: CpStaffRole[] = [
  // Leadership
  {
    id: 'role-head',
    name: 'Headteacher',
    code: 'HEAD',
    description: 'School headteacher',
    category: 'leadership',
    createdAt: now,
  },
  {
    id: 'role-deputy',
    name: 'Deputy Headteacher',
    code: 'DHT',
    description: 'Deputy headteacher',
    category: 'leadership',
    createdAt: now,
  },
  {
    id: 'role-assistant',
    name: 'Assistant Headteacher',
    code: 'AHT',
    description: 'Assistant headteacher',
    category: 'leadership',
    createdAt: now,
  },
  // Teaching
  {
    id: 'role-hod',
    name: 'Head of Department',
    code: 'HOD',
    description: 'Department head',
    category: 'teaching',
    createdAt: now,
  },
  {
    id: 'role-teacher',
    name: 'Teacher',
    code: 'TCH',
    description: 'Classroom teacher',
    category: 'teaching',
    createdAt: now,
  },
  {
    id: 'role-nqt',
    name: 'Newly Qualified Teacher',
    code: 'NQT',
    description: 'Early career teacher',
    category: 'teaching',
    createdAt: now,
  },
  // Pastoral
  {
    id: 'role-hoy',
    name: 'Head of Year',
    code: 'HOY',
    description: 'Head of year group',
    category: 'pastoral',
    createdAt: now,
  },
  {
    id: 'role-tutor',
    name: 'Form Tutor',
    code: 'TUTOR',
    description: 'Form/tutor group leader',
    category: 'pastoral',
    createdAt: now,
  },
];

export const mockCpTeachers: CpTeacher[] = [
  {
    id: 'teacher-001',
    firstName: 'James',
    lastName: 'Wilson',
    initials: 'JW',
    email: 'j.wilson@school.edu',
    employeeCode: 'EMP001',
    fte: 1.0,
    maxPeriodsPerWeek: 22,
    maxPeriodsPerCycle: 22,
    targetPeriods: 20,
    ppaAllocation: 2,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'teacher-002',
    firstName: 'Sarah',
    lastName: 'Thompson',
    initials: 'ST',
    email: 's.thompson@school.edu',
    employeeCode: 'EMP002',
    fte: 1.0,
    maxPeriodsPerWeek: 22,
    maxPeriodsPerCycle: 22,
    targetPeriods: 20,
    ppaAllocation: 2,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'teacher-003',
    firstName: 'Michael',
    lastName: 'Brown',
    initials: 'MB',
    email: 'm.brown@school.edu',
    employeeCode: 'EMP003',
    fte: 1.0,
    maxPeriodsPerWeek: 22,
    maxPeriodsPerCycle: 22,
    targetPeriods: 20,
    ppaAllocation: 2,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'teacher-004',
    firstName: 'Emily',
    lastName: 'Davis',
    initials: 'ED',
    email: 'e.davis@school.edu',
    employeeCode: 'EMP004',
    fte: 0.8,
    maxPeriodsPerWeek: 18,
    maxPeriodsPerCycle: 18,
    targetPeriods: 16,
    ppaAllocation: 2,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'teacher-005',
    firstName: 'Robert',
    lastName: 'Johnson',
    initials: 'RJ',
    email: 'r.johnson@school.edu',
    employeeCode: 'EMP005',
    fte: 1.0,
    maxPeriodsPerWeek: 22,
    maxPeriodsPerCycle: 22,
    targetPeriods: 20,
    ppaAllocation: 2,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'teacher-006',
    firstName: 'Jennifer',
    lastName: 'Martinez',
    initials: 'JM',
    email: 'j.martinez@school.edu',
    employeeCode: 'EMP006',
    fte: 1.0,
    maxPeriodsPerWeek: 22,
    maxPeriodsPerCycle: 22,
    targetPeriods: 18,
    ppaAllocation: 4,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'teacher-007',
    firstName: 'David',
    lastName: 'Garcia',
    initials: 'DG',
    email: 'd.garcia@school.edu',
    employeeCode: 'EMP007',
    fte: 1.0,
    maxPeriodsPerWeek: 22,
    maxPeriodsPerCycle: 22,
    targetPeriods: 20,
    ppaAllocation: 2,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'teacher-008',
    firstName: 'Lisa',
    lastName: 'Anderson',
    initials: 'LA',
    email: 'l.anderson@school.edu',
    employeeCode: 'EMP008',
    fte: 0.6,
    maxPeriodsPerWeek: 14,
    maxPeriodsPerCycle: 14,
    targetPeriods: 12,
    ppaAllocation: 2,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'teacher-009',
    firstName: 'Christopher',
    lastName: 'Taylor',
    initials: 'CT',
    email: 'c.taylor@school.edu',
    employeeCode: 'EMP009',
    fte: 1.0,
    maxPeriodsPerWeek: 22,
    maxPeriodsPerCycle: 22,
    targetPeriods: 20,
    ppaAllocation: 2,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'teacher-010',
    firstName: 'Amanda',
    lastName: 'Thomas',
    initials: 'AT',
    email: 'a.thomas@school.edu',
    employeeCode: 'EMP010',
    fte: 1.0,
    maxPeriodsPerWeek: 22,
    maxPeriodsPerCycle: 22,
    targetPeriods: 20,
    ppaAllocation: 2,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
];

export const mockCpTeacherRoles: CpTeacherRole[] = [
  // Teacher 001 - HOD Maths
  {
    id: 'tr-001-hod',
    teacherId: 'teacher-001',
    roleId: 'role-hod',
    isPrimaryRole: true,
    createdAt: now,
  },
  {
    id: 'tr-001-tutor',
    teacherId: 'teacher-001',
    roleId: 'role-tutor',
    isPrimaryRole: false,
    createdAt: now,
  },
  // Teacher 002 - HOD English
  {
    id: 'tr-002-hod',
    teacherId: 'teacher-002',
    roleId: 'role-hod',
    isPrimaryRole: true,
    createdAt: now,
  },
  // Teacher 003 - HOD Science
  {
    id: 'tr-003-hod',
    teacherId: 'teacher-003',
    roleId: 'role-hod',
    isPrimaryRole: true,
    createdAt: now,
  },
  {
    id: 'tr-003-hoy',
    teacherId: 'teacher-003',
    roleId: 'role-hoy',
    isPrimaryRole: false,
    createdAt: now,
  },
  // Teacher 004-010 - Regular teachers
  ...['teacher-004', 'teacher-005', 'teacher-006', 'teacher-007', 'teacher-008', 'teacher-009', 'teacher-010'].map(
    (teacherId, idx) => ({
      id: `tr-${String(idx + 4).padStart(3, '0')}-teacher`,
      teacherId,
      roleId: 'role-teacher',
      isPrimaryRole: true,
      createdAt: now,
    })
  ),
];

export const mockCpTeacherSubjects: CpTeacherSubject[] = [
  // Teacher 001 - Maths
  {
    id: 'ts-001-maths',
    teacherId: 'teacher-001',
    subjectId: 'subj-maths',
    isQualified: true,
    isApproved: true,
    periodsAllocated: 20,
    createdAt: now,
  },
  // Teacher 002 - English
  {
    id: 'ts-002-eng',
    teacherId: 'teacher-002',
    subjectId: 'subj-english',
    isQualified: true,
    isApproved: true,
    periodsAllocated: 20,
    createdAt: now,
  },
  // Teacher 003 - Science
  {
    id: 'ts-003-sci',
    teacherId: 'teacher-003',
    subjectId: 'subj-science',
    isQualified: true,
    isApproved: true,
    periodsAllocated: 18,
    createdAt: now,
  },
  // Teacher 004 - History
  {
    id: 'ts-004-hist',
    teacherId: 'teacher-004',
    subjectId: 'subj-history',
    isQualified: true,
    isApproved: true,
    periodsAllocated: 16,
    createdAt: now,
  },
  // Teacher 005 - Geography
  {
    id: 'ts-005-geog',
    teacherId: 'teacher-005',
    subjectId: 'subj-geography',
    isQualified: true,
    isApproved: true,
    periodsAllocated: 20,
    createdAt: now,
  },
  // Teacher 006 - PE
  {
    id: 'ts-006-pe',
    teacherId: 'teacher-006',
    subjectId: 'subj-pe',
    isQualified: true,
    isApproved: true,
    periodsAllocated: 18,
    createdAt: now,
  },
  // Teacher 007 - French
  {
    id: 'ts-007-fre',
    teacherId: 'teacher-007',
    subjectId: 'subj-mfl-french',
    isQualified: true,
    isApproved: true,
    periodsAllocated: 20,
    createdAt: now,
  },
  // Teacher 008 - Art
  {
    id: 'ts-008-art',
    teacherId: 'teacher-008',
    subjectId: 'subj-art',
    isQualified: true,
    isApproved: true,
    periodsAllocated: 12,
    createdAt: now,
  },
  // Teacher 009 - Computing
  {
    id: 'ts-009-comp',
    teacherId: 'teacher-009',
    subjectId: 'subj-computing',
    isQualified: true,
    isApproved: true,
    periodsAllocated: 20,
    createdAt: now,
  },
  // Teacher 010 - Music
  {
    id: 'ts-010-mus',
    teacherId: 'teacher-010',
    subjectId: 'subj-music',
    isQualified: true,
    isApproved: true,
    periodsAllocated: 20,
    createdAt: now,
  },
];
